import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from 'components/ui/button'
import { LoaderMain } from 'components/loader'
import { useNotificationBar } from 'context/notificationContext'
import {
  usePaymentMethods,
  useUpdatePaymentMethods,
} from 'hooks/custom-checkout'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { getErrorResponse, normalizeIconPath } from 'services/util'
import { QueryKeys } from 'hooks/_query_keys'
import {
  createPaymentMethod,
  defaultCustomCheckoutValues,
} from './defaultValues'
import { customCheckoutSchema } from 'validations/customCheckout.validation'
import { CustomCheckoutFormValues, PaymentMethodItem } from './types'
import InfoBanner from 'components/InfoBanner'
import PaymentMethodEditor from 'components/CustomCheckout/PaymentMethodEditor'
import SectionCard from 'components/CustomCheckout/SectionCard'
import SortableRepeater from 'components/CustomCheckout/SortableRepeater'
import { generatePaymentMethodSlug } from './utils'

const extractPaymentMethods = (response: any): PaymentMethodItem[] => {
  const body = response?.data?.data ?? response?.data ?? response
  const methods = Array.isArray(body) ? body : body?.paymentMethods ?? []

  return methods.map((method: any) => ({
    id: method?.id != null ? String(method.id) : createPaymentMethod().id,
    name: method?.name ?? '',
    icon: method?.icon ?? '',
    slug: method?.slug ?? generatePaymentMethodSlug(method?.name ?? ''),
    enabled: method?.enabled ?? true,
    isNew: false,
  }))
}

const toFormValues = (paymentMethods: PaymentMethodItem[]): CustomCheckoutFormValues => ({
  ...defaultCustomCheckoutValues,
  bottomSheet: {
    ...defaultCustomCheckoutValues.bottomSheet,
    paymentMethods,
  },
})

const PaymentMethod = () => {
  const notification = useNotificationBar()
  const queryClient = useQueryClient()
  const [expandedPaymentMethods, setExpandedPaymentMethods] = useState<Record<string, boolean>>({})

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<CustomCheckoutFormValues>({
    resolver: yupResolver(customCheckoutSchema),
    mode: 'onChange',
    defaultValues: defaultCustomCheckoutValues,
  })

  const {
    data: paymentMethodResponse,
    isLoading: isLoadingPaymentMethods,
    isError: isPaymentMethodError,
    error: paymentMethodError,
  } = usePaymentMethods()

  const { mutate: updatePaymentMethods, isLoading: isUpdating } =
    useUpdatePaymentMethods()

  const isSaving = isUpdating

  useEffect(() => {
    if (!paymentMethodResponse || isPaymentMethodError) return

    const formValues = toFormValues(extractPaymentMethods(paymentMethodResponse))
    reset(formValues)
  }, [paymentMethodResponse, isPaymentMethodError, reset])

  useEffect(() => {
    if (!isPaymentMethodError) return
    notification.push(getErrorResponse(paymentMethodError))
  }, [isPaymentMethodError, paymentMethodError, notification])

  const formValues = watch()

  const togglePaymentExpand = useCallback((id: string) => {
    setExpandedPaymentMethods((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const paymentMethods = formValues.bottomSheet?.paymentMethods ?? []
  const handlePaymentMethodsChange = useCallback(
    (items: CustomCheckoutFormValues['bottomSheet']['paymentMethods']) => {
      setValue('bottomSheet.paymentMethods', items, {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [setValue]
  )

  const onSave = handleSubmit((data) => {
    const payload = {
      paymentMethods: data.bottomSheet.paymentMethods.map((method, index) => ({
        id: method.isNew ? undefined : method.id,
        name: method.name,
        slug: method.slug,
        icon: normalizeIconPath(method.icon),
        enabled: method.enabled,
        sortOrder: index,
      })),
    }

    const onSuccess = (response: any) => {
      const formValues = toFormValues(extractPaymentMethods(response))

      reset(formValues)
      setExpandedPaymentMethods({})

      queryClient.invalidateQueries([QueryKeys.CUSTOM_CHECKOUT, 'payment-methods'])

      notification.push({
        type: 'success',
        title: 'Saved',
        message: 'Payment methods saved successfully.',
      })
    }

    const onError = (error: any) => {
      notification.push(getErrorResponse(error))
    }

    updatePaymentMethods({ data: payload }, { onSuccess, onError })
  })

  if (isLoadingPaymentMethods) {
    return <LoaderMain size="md" text="Loading payment methods..." fullscreen />
  }

  return (
    <div className="w-full bg-gray-50 px-6 py-6 dark:bg-slate-900">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
          Payment Method Configuration
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Configure payment methods used by checkout.
        </p>
      </div>

      <InfoBanner message="Only payment method configuration is managed here." />

      <form className="space-y-8" onSubmit={(event) => event.preventDefault()}>
        <SectionCard
          title="Payment Methods"
          description="Configure and reorder available payment methods displayed in checkout."
          badge="Section 1"
        >
          <SortableRepeater
            items={paymentMethods}
            onChange={handlePaymentMethodsChange}
            getDragLabel={(item) => item.name || 'Payment method'}
            renderItem={(item, index) => (
              <PaymentMethodEditor
                key={item.id}
                id={item.id}
                index={index}
                control={control}
                register={register}
                errors={errors}
                name={item.name}
                enabled={item.enabled}
                expanded={expandedPaymentMethods[item.id] ?? index === 0}
                onToggleExpand={() => togglePaymentExpand(item.id)}
                onEnabledChange={(enabled) => {
                  const next = [...paymentMethods]
                  next[index] = { ...next[index], enabled }
                  handlePaymentMethodsChange(next)
                }}
              />
            )}
          />
        </SectionCard>
        <div className="sticky bottom-0 z-10 flex justify-end gap-3 border-t border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <Button
            type="button"
            color="blue"
            onClick={onSave}
            disabled={!isDirty || !isValid || isSaving}
            isLoading={isSaving}
            loadinText="Saving..."
          >
            Save
          </Button>
        </div>
      </form>
    </div>
  )
}

export default PaymentMethod
